import { supabase } from './supabaseClient'

export interface ChatMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: Date
}

export interface ChatResponse {
  message: string
}

export interface ChatConversation {
  id: string
  applicationId: string | null  // null for general portfolio chat
  title: string | null
  createdAt: Date
  updatedAt: Date
}

export interface ChatConversationPreview {
  id: string  // conversation ID (primary key)
  applicationId: string | null  // null for general portfolio chat
  title: string | null
  lastMessage: string
  lastMessageAt: Date
  messageCount: number
}

/**
 * Create a new conversation for an application (or general portfolio chat if applicationId is null)
 */
export async function createConversation(applicationId: string | null, title?: string): Promise<ChatConversation> {
  const { data: { user }, error: userError } = await supabase.auth.getUser()

  if (userError || !user) {
    throw new Error('No active session. Please log in again.')
  }

  const { data, error } = await supabase
    .from('chat_conversations')
    .insert({
      user_id: user.id,
      application_id: applicationId,  // null for general chat
      title: title || null,
    })
    .select('id, application_id, title, created_at, updated_at')
    .single()

  if (error) {
    console.error('Failed to create conversation:', error)
    throw new Error('Failed to create conversation')
  }

  return {
    id: data.id,
    applicationId: data.application_id,
    title: data.title,
    createdAt: new Date(data.created_at),
    updatedAt: new Date(data.updated_at),
  }
}

/**
 * Send a message to the AI chat service
 * @param applicationId - The application ID, or null for general portfolio chat
 */
export async function sendChatMessage(
  applicationId: string | null,
  message: string,
  history: ChatMessage[]
): Promise<ChatResponse> {
  const { data: { user }, error: userError } = await supabase.auth.getUser()

  if (userError || !user) {
    throw new Error('No active session. Please log in again.')
  }

  const { data, error } = await supabase.functions.invoke<ChatResponse>('chat-with-ai', {
    body: {
      applicationId,
      message,
      history: history.map(h => ({ role: h.role, content: h.content })),
    },
  })

  if (error) {
    const msg = (error as { message?: string }).message || 'Failed to send message'
    throw new Error(msg)
  }

  if (!data?.message) {
    throw new Error('No response from AI')
  }

  return data
}

/**
 * Load chat history for a conversation from the database
 */
export async function loadChatHistory(conversationId: string): Promise<ChatMessage[]> {
  const { data: { user }, error: userError } = await supabase.auth.getUser()

  if (userError || !user) {
    throw new Error('No active session. Please log in again.')
  }

  const { data, error } = await supabase
    .from('chat_messages')
    .select('id, role, content, created_at')
    .eq('conversation_id', conversationId)
    .order('created_at', { ascending: true })

  if (error) {
    console.error('Failed to load chat history:', error)
    throw new Error('Failed to load chat history')
  }

  return (data || []).map(msg => ({
    id: msg.id,
    role: msg.role as 'user' | 'assistant',
    content: msg.content,
    timestamp: new Date(msg.created_at),
  }))
}

/**
 * Save a chat message to a conversation
 */
export async function saveChatMessage(
  conversationId: string,
  role: 'user' | 'assistant',
  content: string
): Promise<ChatMessage> {
  const { data: { user }, error: userError } = await supabase.auth.getUser()

  if (userError || !user) {
    throw new Error('No active session. Please log in again.')
  }

  const { data, error } = await supabase
    .from('chat_messages')
    .insert({
      conversation_id: conversationId,
      role,
      content,
    })
    .select('id, role, content, created_at')
    .single()

  if (error) {
    console.error('Failed to save chat message:', error)
    throw new Error('Failed to save chat message')
  }

  // Update conversation's updated_at timestamp (fire-and-forget)
  supabase
    .from('chat_conversations')
    .update({ updated_at: new Date().toISOString() })
    .eq('id', conversationId)
    .then(({ error }) => {
      if (error) console.error('Failed to update conversation timestamp:', error)
    })

  return {
    id: data.id,
    role: data.role as 'user' | 'assistant',
    content: data.content,
    timestamp: new Date(data.created_at),
  }
}

/**
 * Delete a conversation and all its messages
 */
export async function deleteConversation(conversationId: string): Promise<void> {
  const { data: { user }, error: userError } = await supabase.auth.getUser()

  if (userError || !user) {
    throw new Error('No active session. Please log in again.')
  }

  const { error } = await supabase
    .from('chat_conversations')
    .delete()
    .eq('id', conversationId)

  if (error) {
    console.error('Failed to delete conversation:', error)
    throw new Error('Failed to delete conversation')
  }
}

/**
 * Get all conversations with preview info (last message, count)
 * Returns conversations sorted by most recent activity
 */
export async function getChatConversations(): Promise<ChatConversationPreview[]> {
  const { data: { user }, error: userError } = await supabase.auth.getUser()

  if (userError || !user) {
    return []
  }

  // Get all conversations
  const { data: conversations, error: convError } = await supabase
    .from('chat_conversations')
    .select('id, application_id, title, created_at, updated_at')
    .eq('user_id', user.id)
    .order('updated_at', { ascending: false })

  if (convError) {
    console.error('Failed to get conversations:', convError)
    return []
  }

  if (!conversations || conversations.length === 0) {
    return []
  }

  // Get message counts and last messages for each conversation
  const conversationIds = conversations.map(c => c.id)
  
  const { data: messages, error: msgError } = await supabase
    .from('chat_messages')
    .select('conversation_id, content, created_at')
    .in('conversation_id', conversationIds)
    .order('created_at', { ascending: false })

  if (msgError) {
    console.error('Failed to get conversation messages:', msgError)
  }

  // Build preview data
  const messagesByConversation = new Map<string, { count: number; lastMessage: string; lastAt: Date }>()
  for (const msg of messages || []) {
    const existing = messagesByConversation.get(msg.conversation_id)
    if (!existing) {
      messagesByConversation.set(msg.conversation_id, {
        count: 1,
        lastMessage: msg.content.slice(0, 80) + (msg.content.length > 80 ? '...' : ''),
        lastAt: new Date(msg.created_at),
      })
    } else {
      existing.count++
    }
  }

  return conversations.map(conv => {
    const msgData = messagesByConversation.get(conv.id)
    return {
      id: conv.id,
      applicationId: conv.application_id,
      title: conv.title,
      lastMessage: msgData?.lastMessage || '',
      lastMessageAt: msgData?.lastAt || new Date(conv.updated_at),
      messageCount: msgData?.count || 0,
    }
  })
}

// Legacy function - kept for backward compatibility during migration
export async function clearChatHistory(conversationId: string): Promise<void> {
  return deleteConversation(conversationId)
}

// Legacy function - kept for backward compatibility
export async function getAppsWithChatHistory(): Promise<string[]> {
  const conversations = await getChatConversations()
  return [...new Set(conversations.map(c => c.applicationId).filter((id): id is string => id !== null))]
}
